import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { queryKeys } from "@/lib/queryClient";
import { useClientsList } from "@/hooks/useClients";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { useHasModule } from "@/hooks/usePermissions";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import InCardFilterPanel from "@/components/leads/InCardFilterPanel";
import LeadTableColumnsDialog from "@/components/leads/LeadTableColumnsDialog";
import CustomViewsDialog from "@/components/leads/CustomViewsDialog";

import { ArrowDownAZ, ArrowUpAZ, ArrowUpDown, ChevronDown, Download, ListFilter, RefreshCw, Search, Settings2, Trash2, AlertTriangle, Briefcase } from "lucide-react";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

const DEFAULT_LIMIT = 10;

const CLIENT_FIELDS = [
  "id",
  "team_id",
  "household_id",
  "primary_lead_id",
  "full_name",
  "phone_number",
  "email",
  "state",
  "insurance_type",
  "company",
  "product_name",
  "product_status",
  "product_sub_status",
  "issue_age",
  "application_date",
  "coverage_length",
  "annual_premium",
  "annual_target_premium",
  "monthly_premium",
  "death_benefit",
  "created_at",
  "updated_at",
];

const CLIENT_FIELD_OPTIONS = CLIENT_FIELDS.map((key) => ({
  key,
  label: prettifyFieldLabel(key),
}));

const DEFAULT_TABLE_COLUMNS = [
  "full_name",
  "phone_number",
  "email",
  "state",
  "insurance_type",
  "company",
  "product_name",
  "application_date",
  "created_at",
];

const DEFAULT_VISIBLE_FILTERS = [
  "created_at",
  "state",
  "insurance_type",
  "company",
  "product_name",
  "product_status",
  "product_sub_status",
  "full_name",
  "application_date",
];

const DATE_FIELDS = new Set(["created_at", "updated_at", "application_date"]);
const SELECT_FIELDS = new Set([
  "state",
  "insurance_type",
  "company",
  "product_name",
  "product_status",
  "product_sub_status",
]);

const PILL_CLASSES = [
  "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20",
  "bg-emerald-500/20 text-emerald-100 ring-emerald-400/20",
  "bg-sky-500/20 text-sky-100 ring-sky-400/20",
  "bg-amber-500/20 text-amber-100 ring-amber-400/20",
  "bg-fuchsia-500/20 text-fuchsia-100 ring-fuchsia-400/20",
  "bg-rose-500/20 text-rose-100 ring-rose-400/20",
  "bg-violet-500/20 text-violet-100 ring-violet-400/20",
];

const SEARCH_FIELDS = [
  "full_name",
  "phone_number",
  "email",
  "state",
  "company",
  "product_name",
  "product_status",
  "product_sub_status",
  "insurance_type",
];

function hashToIndex(s) {
  const str = String(s || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h % PILL_CLASSES.length;
}

function pillClass(value) {
  if (!value || value === "—") return PILL_CLASSES[0];
  return PILL_CLASSES[hashToIndex(value)];
}

function productStatusPill(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "bg-green-500/20 text-green-100 ring-green-400/25";
  if (s === "pending") return "bg-blue-500/20 text-blue-100 ring-blue-400/25";
  if (s === "decline" || s === "declined") return "bg-red-500/20 text-red-100 ring-red-400/25";
  if (s === "cancelled" || s === "canceled") return "bg-red-500/20 text-red-100 ring-red-400/25";
  if (s === "withdrawn") return "bg-amber-500/20 text-amber-100 ring-amber-400/25";
  return "bg-white/10 text-slate-100 ring-white/10";
}

function formatMmddyyyy(value) {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }
  return s;
}

function prettifyFieldLabel(key) {
  if (!key) return "";
  const s = String(key);
  if (s === "dob") return "DOB";
  if (s === "primary_lead_id") return "Primary Lead ID";
  if (s === "team_id") return "Account ID";
  if (s === "annual_target_premium") return "Annual Target Premium";
  if (s === "product_sub_status") return "Product Sub-Status";
  if (s === "application_date") return "Application Create Date";
  return s
    .split("_")
    .filter(Boolean)
    .map((w) => {
      const lw = w.toLowerCase();
      if (lw === "id") return "ID";
      if (lw === "api") return "API";
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join(" ");
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function toCsvCell(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return '"' + s.replaceAll('"', '""') + '"';
  }
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(toCsvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function pickLatestSoldPayload(payloads = []) {
  if (!Array.isArray(payloads) || payloads.length === 0) return null;
  return payloads.reduce((latest, current) => {
    if (!latest) return current;
    const latestDate = new Date(latest.application_date || latest.created_at || 0).getTime();
    const currentDate = new Date(current.application_date || current.created_at || 0).getTime();
    if (Number.isNaN(latestDate) && Number.isNaN(currentDate)) return latest;
    if (Number.isNaN(latestDate)) return current;
    if (Number.isNaN(currentDate)) return latest;
    return currentDate > latestDate ? current : latest;
  }, null);
}

function getClientFieldValue(client, key) {
  if (!client) return "";
  if (client[key] !== undefined) return client[key];
  const payload = client.sold_payload || pickLatestSoldPayload(client.sold_payloads);
  if (payload && payload[key] !== undefined) return payload[key];
  return "";
}

function formatCellValue(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (DATE_FIELDS.has(key)) return formatMmddyyyy(value);
  return String(value);
}

function toDateOnly(date) {
  if (!date) return null;
  if (typeof date === "string") {
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfWeek(date) {
  const start = startOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

function matchesDateOperator(value, operator, row) {
  const dateValue = toDateOnly(value);
  if (!dateValue) return false;
  const now = new Date();
  const today = toDateOnly(now);

  const valueDate = toDateOnly(row.value);
  const valueToDate = toDateOnly(row.value_to);

  if (operator === "on" && valueDate) return dateValue.getTime() === valueDate.getTime();
  if (operator === "between" && valueDate && valueToDate) {
    return dateValue.getTime() >= valueDate.getTime() && dateValue.getTime() <= valueToDate.getTime();
  }
  if (operator === "before" && valueDate) return dateValue.getTime() < valueDate.getTime();
  if (operator === "after" && valueDate) return dateValue.getTime() > valueDate.getTime();
  if (operator === "this_week" && today) return dateValue >= startOfWeek(today) && dateValue <= endOfWeek(today);
  if (operator === "last_week" && today) {
    const lastWeekEnd = new Date(startOfWeek(today));
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
    const lastWeekStart = startOfWeek(lastWeekEnd);
    return dateValue >= lastWeekStart && dateValue <= endOfWeek(lastWeekEnd);
  }
  if (operator === "this_month" && today) {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return dateValue >= start && dateValue <= end;
  }
  if (operator === "last_month" && today) {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return dateValue >= start && dateValue <= end;
  }
  if (operator === "this_year" && today) {
    const start = new Date(today.getFullYear(), 0, 1);
    const end = new Date(today.getFullYear(), 11, 31);
    return dateValue >= start && dateValue <= end;
  }
  if (operator === "last_year" && today) {
    const start = new Date(today.getFullYear() - 1, 0, 1);
    const end = new Date(today.getFullYear() - 1, 11, 31);
    return dateValue >= start && dateValue <= end;
  }
  if (operator === "last_30_days" && today) {
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    return dateValue >= start && dateValue <= today;
  }
  if (operator === "last_60_days" && today) {
    const start = new Date(today);
    start.setDate(start.getDate() - 60);
    return dateValue >= start && dateValue <= today;
  }
  if (operator === "last_90_days" && today) {
    const start = new Date(today);
    start.setDate(start.getDate() - 90);
    return dateValue >= start && dateValue <= today;
  }
  if (operator === "ytd" && today) {
    const start = new Date(today.getFullYear(), 0, 1);
    return dateValue >= start && dateValue <= today;
  }
  return true;
}

function matchesFilter(value, row, type) {
  const operator = row.operator;
  if (!operator) return true;

  const str = String(value ?? "").trim();
  const lower = str.toLowerCase();

  if (operator === "is_empty") return lower.length === 0;
  if (operator === "is_not_empty") return lower.length > 0;

  if (type === "date") {
    return matchesDateOperator(value, operator, row);
  }

  if (type === "select") {
    const values = Array.isArray(row.values) ? row.values.filter(Boolean) : [];
    if (values.length === 0) return true;
    if (operator === "is") return values.includes(str);
    if (operator === "is_not") return !values.includes(str);
    return true;
  }

  const needle = String(row.value || "").trim().toLowerCase();
  if (!needle) return true;
  if (operator === "is") return lower === needle;
  if (operator === "isnt") return lower !== needle;
  if (operator === "contains") return lower.includes(needle);
  if (operator === "not_contains") return !lower.includes(needle);
  if (operator === "starts_with") return lower.startsWith(needle);
  if (operator === "ends_with") return lower.endsWith(needle);
  return true;
}

export default function ClientsPage() {
  usePageTitle("Clients");
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // P0 TENANT ISOLATION: Subscribe to tenant context for automatic refetch
  const { selectedTenantId } = useTenant();
  
  // P0 TENANT ISOLATION: Use centralized tenant path builder
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  // Function to invalidate clients cache
  const invalidateClientsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.clients.all(selectedTenantId),
    });
  }, [queryClient, selectedTenantId]);

  const [items, setItems] = useState([]);

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  const [zohoFiltersOpen, setZohoFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("clients_filters_open");
      return stored ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("clients_filters_open", String(zohoFiltersOpen));
    } catch {
      // ignore
    }
  }, [zohoFiltersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  const [clientsPrefs, setClientsPrefs] = useState({
    visible_filters: DEFAULT_VISIBLE_FILTERS,
    table_columns: DEFAULT_TABLE_COLUMNS,
    all_table_columns: DEFAULT_TABLE_COLUMNS,
    custom_views: [],
    active_custom_view_id: "all",
    page_size: DEFAULT_LIMIT,
    // P0 FIX: Store columns per view for persistence
    view_columns: {}, // { [view_id]: columns[] }
  });
  const [prefsLoading, setPrefsLoading] = useState(true);

  const [customViewsOpen, setCustomViewsOpen] = useState(false);
  const [activeCustomViewId, setActiveCustomViewId] = useState("all");

  const [columnsOpen, setColumnsOpen] = useState(false);

  // P0 FIX: Generate tenant-scoped storage key for column persistence
  const getPrefsStorageKey = useCallback(() => {
    return selectedTenantId 
      ? `clients_prefs_${selectedTenantId}` 
      : "clients_prefs";
  }, [selectedTenantId]);

  // P0 FIX: Load preferences on mount and when tenant changes
  useEffect(() => {
    setPrefsLoading(true);
    try {
      const storageKey = getPrefsStorageKey();
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setClientsPrefs((prev) => ({ ...prev, ...parsed }));
        if (parsed.active_custom_view_id) {
          setActiveCustomViewId(parsed.active_custom_view_id);
        }
      } else {
        // Reset to defaults when switching to a tenant with no saved prefs
        setClientsPrefs({
          visible_filters: DEFAULT_VISIBLE_FILTERS,
          table_columns: DEFAULT_TABLE_COLUMNS,
          all_table_columns: DEFAULT_TABLE_COLUMNS,
          custom_views: [],
          active_custom_view_id: "all",
          page_size: DEFAULT_LIMIT,
          view_columns: {},
        });
        setActiveCustomViewId("all");
      }
    } catch {
      // ignore
    } finally {
      setPrefsLoading(false);
    }
  }, [getPrefsStorageKey]);

  // P0 FIX: Save preferences with tenant-scoped key
  const saveClientsPrefs = useCallback((next) => {
    setClientsPrefs(next);
    try {
      const storageKey = getPrefsStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore
    }
  }, [getPrefsStorageKey]);

  // P0 FIX: customViews must be declared before tableColumns useMemo that references it
  const customViews = Array.isArray(clientsPrefs.custom_views) ? clientsPrefs.custom_views : [];

  // P0 FIX: Compute tableColumns with per-view persistence
  // Priority: 1) view_columns[activeView], 2) all_table_columns (for "all" view), 3) DEFAULT
  const tableColumns = useMemo(() => {
    const viewColumns = clientsPrefs.view_columns || {};
    
    // Check if we have saved columns for this specific view
    if (viewColumns[activeCustomViewId] && Array.isArray(viewColumns[activeCustomViewId]) && viewColumns[activeCustomViewId].length > 0) {
      return viewColumns[activeCustomViewId];
    }
    
    // For "all" view, use all_table_columns or table_columns
    if (activeCustomViewId === "all") {
      if (Array.isArray(clientsPrefs.all_table_columns) && clientsPrefs.all_table_columns.length > 0) {
        return clientsPrefs.all_table_columns;
      }
      if (Array.isArray(clientsPrefs.table_columns) && clientsPrefs.table_columns.length > 0) {
        return clientsPrefs.table_columns;
      }
    }
    
    // For custom views, check if view has columns defined
    const customView = customViews.find(v => v.id === activeCustomViewId);
    if (customView && Array.isArray(customView.columns) && customView.columns.length > 0) {
      return customView.columns;
    }
    
    // Fallback to table_columns or default
    if (Array.isArray(clientsPrefs.table_columns) && clientsPrefs.table_columns.length > 0) {
      return clientsPrefs.table_columns;
    }
    
    return DEFAULT_TABLE_COLUMNS;
  }, [activeCustomViewId, clientsPrefs.view_columns, clientsPrefs.all_table_columns, clientsPrefs.table_columns, customViews]);

  useEffect(() => {
    if (prefsLoading) return;
    saveClientsPrefs({ ...clientsPrefs, active_custom_view_id: activeCustomViewId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomViewId, saveClientsPrefs]);

  const filterFieldDefs = useMemo(() => {
    return CLIENT_FIELDS.map((key) => {
      let type = "text";
      if (SELECT_FIELDS.has(key)) type = "select";
      if (DATE_FIELDS.has(key)) type = "date";
      return { key, label: prettifyFieldLabel(key), type };
    }).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, []);

  const filterOptions = useMemo(() => {
    const opts = {
      state: new Set(),
      insurance_type: new Set(),
      company: new Set(),
      product_name: new Set(),
      product_status: new Set(),
      product_sub_status: new Set(),
    };
    items.forEach((client) => {
      const state = getClientFieldValue(client, "state");
      const insuranceType = getClientFieldValue(client, "insurance_type");
      const company = getClientFieldValue(client, "company");
      const productName = getClientFieldValue(client, "product_name");
      const productStatus = getClientFieldValue(client, "product_status");
      const productSubStatus = getClientFieldValue(client, "product_sub_status");
      if (state) opts.state.add(state);
      if (insuranceType) opts.insurance_type.add(insuranceType);
      if (company) opts.company.add(company);
      if (productName) opts.product_name.add(productName);
      if (productStatus) opts.product_status.add(productStatus);
      if (productSubStatus) opts.product_sub_status.add(productSubStatus);
    });
    return {
      state: Array.from(opts.state).sort(),
      insurance_type: Array.from(opts.insurance_type).sort(),
      company: Array.from(opts.company).sort(),
      product_name: Array.from(opts.product_name).sort(),
      product_status: Array.from(opts.product_status).sort(),
      product_sub_status: Array.from(opts.product_sub_status).sort(),
    };
  }, [items]);

  const optionsByField = useMemo(
    () => ({
      state: filterOptions.state,
      insurance_type: filterOptions.insurance_type,
      company: filterOptions.company,
      product_name: filterOptions.product_name,
      product_status: filterOptions.product_status,
      product_sub_status: filterOptions.product_sub_status,
    }),
    [filterOptions]
  );

  const filtersActive = useMemo(() => Object.keys(filtersApplied || {}).length > 0, [filtersApplied]);

  const activeViewLabel = useMemo(() => {
    if (activeCustomViewId === "all") return "All Clients";
    const v = (customViews || []).find((x) => x.id === activeCustomViewId);
    return v?.name || "Custom View";
  }, [activeCustomViewId, customViews]);

  const applyZohoDraft = () => {
    setFiltersApplied(filtersDraft);
    setOffset(0);
  };

  const clearZohoFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
    setOffset(0);
  };

  useEffect(() => {
    setFiltersDraft(filtersApplied);
  }, [filtersApplied]);

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (typeof clientsPrefs.page_size === "number") {
      setLimit(clientsPrefs.page_size);
    }
  }, [clientsPrefs.page_size]);

  // MA-PHASE10: Use module-based permissions
  // MA-RBAC-POST-BATCH3-FRONTEND-CONSISTENCY-AUDIT-124: canonical 'clients' module
  const { hasAccess: canManageClients } = useHasModule("clients");

  const [selected, setSelected] = useState(() => new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState("selected");
  const [exportFields, setExportFields] = useState(() => new Set(CLIENT_FIELDS));
  const [exporting, setExporting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Extract date filter from applied filters for backend query
  const getDateFilterParams = (filters) => {
    for (const [field, row] of Object.entries(filters || {})) {
      if (row?.enabled && DATE_FIELDS.has(field)) {
        return {
          field,
          operator: row.operator,
          value: row.value,
          value_to: row.value_to,
        };
      }
    }
    return {};
  };

  // Get date filter params from current filters
  const dateParams = useMemo(() => getDateFilterParams(filtersApplied), [filtersApplied]);

  // ===== REACT QUERY HOOKS =====
  
  // Clients list query
  const clientsQuery = useClientsList({
    dateField: dateParams.field || null,
    dateOperator: dateParams.operator || null,
    dateValue: dateParams.value || null,
    dateValueTo: dateParams.value_to || null,
    enabled: !prefsLoading,
  });

  // Sync React Query data to local state for component compatibility
  useEffect(() => {
    if (clientsQuery.data) {
      setItems(clientsQuery.data || []);
    }
  }, [clientsQuery.data]);

  // Combined loading state
  const loading = prefsLoading || clientsQuery.isLoading;

  // REACT QUERY MIGRATION: Removed manual fetch effects
  // - fetchClients replaced by useClientsList hook
  // - location.key, window focus, and custom event refetch logic no longer needed
  // - React Query handles cache invalidation automatically

  const filteredItems = useMemo(() => {
    let result = items.slice();

    if (qDebounced.trim()) {
      const needle = qDebounced.trim().toLowerCase();
      result = result.filter((client) =>
        SEARCH_FIELDS.some((field) => String(getClientFieldValue(client, field) || "").toLowerCase().includes(needle))
      );
    }

    if (filtersApplied && Object.keys(filtersApplied).length > 0) {
      result = result.filter((client) => {
        return Object.entries(filtersApplied).every(([field, row]) => {
          if (!row?.enabled) return true;
          // Skip date fields - they are handled by backend filtering
          if (DATE_FIELDS.has(field)) return true;
          const type = SELECT_FIELDS.has(field) ? "select" : "text";
          const value = getClientFieldValue(client, field);
          return matchesFilter(value, row, type);
        });
      });
    }

    if (sortBy) {
      result.sort((a, b) => {
        const av = getClientFieldValue(a, sortBy);
        const bv = getClientFieldValue(b, sortBy);
        if (DATE_FIELDS.has(sortBy)) {
          const ad = toDateOnly(av)?.getTime() || 0;
          const bd = toDateOnly(bv)?.getTime() || 0;
          return sortDir === "asc" ? ad - bd : bd - ad;
        }
        const aStr = String(av ?? "").toLowerCase();
        const bStr = String(bv ?? "").toLowerCase();
        if (aStr === bStr) return 0;
        const comp = aStr.localeCompare(bStr);
        return sortDir === "asc" ? comp : -comp;
      });
    }

    return result;
  }, [items, qDebounced, filtersApplied, sortBy, sortDir]);

  const total = filteredItems.length;
  const pagedItems = useMemo(() => filteredItems.slice(offset, offset + limit), [filteredItems, offset, limit]);

  useEffect(() => {
    setSelected(new Set());
  }, [pagedItems]);

  const allOnPageSelected = useMemo(
    () => pagedItems.length > 0 && pagedItems.every((x) => selected.has(x.id)),
    [pagedItems, selected]
  );
  const anySelected = selected.size > 0;
  const selectedRows = useMemo(() => items.filter((client) => selected.has(client.id)), [items, selected]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pagedItems.forEach((x) => next.delete(x.id));
      } else {
        pagedItems.forEach((x) => next.add(x.id));
      }
      return next;
    });
  };

  const openExportDialog = () => {
    setExportScope(anySelected ? "selected" : "filtered");
    setExportFields(new Set(CLIENT_FIELDS));
    setExportOpen(true);
  };

  const runExport = async () => {
    const fieldsOrdered = CLIENT_FIELDS.filter((f) => exportFields.has(f));
    if (fieldsOrdered.length === 0) {
      toast({ title: "Choose at least one field", variant: "destructive" });
      return;
    }
    if (exportScope === "selected" && selected.size === 0) {
      toast({ title: "No clients selected", description: "Select one or more clients first.", variant: "destructive" });
      return;
    }
    setExporting(true);
    try {
      const rows = [fieldsOrdered];
      const data = exportScope === "selected" ? filteredItems.filter((c) => selected.has(c.id)) : filteredItems;
      data.forEach((client) => {
        rows.push(fieldsOrdered.map((k) => getClientFieldValue(client, k) ?? ""));
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = exportScope === "selected" ? `clients_selected_${stamp}.csv` : `clients_filtered_${stamp}.csv`;
      downloadCsv(filename, rows);
      toast({ title: "Export downloaded" });
      setExportOpen(false);
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const onDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedRows.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(selectedRows.map((client) => api.delete(`/clients/${client.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      // REACT QUERY: Invalidate clients cache
      invalidateClientsCache();
      setDeleteConfirmOpen(false);
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
    setOffset(0);
  };

  const clearSort = () => {
    setSortBy(null);
    setSortDir("desc");
    setOffset(0);
  };

  const openColumnsDialog = () => setColumnsOpen(true);

  const clientsCardRef = React.useRef(null);

  const columnsGridStyle = useMemo(() => {
    const n = tableColumns.length;
    return {
      gridTemplateColumns: `repeat(${n}, minmax(160px, 1fr))`,
      minWidth: `${n * 160}px`,
    };
  }, [tableColumns]);

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="clients-page">
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0" data-testid="clients-controls-row">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="clients-custom-view"
                aria-label="Custom view"
              >
                {activeViewLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100">
              <DropdownMenuItem
                onClick={() => {
                  setActiveCustomViewId("all");
                  setFiltersApplied({});
                  setFiltersDraft({});
                  const allColumns = Array.isArray(clientsPrefs.all_table_columns) && clientsPrefs.all_table_columns.length > 0
                    ? clientsPrefs.all_table_columns
                    : DEFAULT_TABLE_COLUMNS;
                  setClientsPrefs((prev) => ({ ...prev, table_columns: allColumns, active_custom_view_id: "all" }));
                  saveClientsPrefs({ ...clientsPrefs, active_custom_view_id: "all", table_columns: allColumns });
                  setOffset(0);
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="clients-custom-view-all"
              >
                All Clients
              </DropdownMenuItem>
              {(customViews || []).map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => {
                    setActiveCustomViewId(v.id);
                    setFiltersApplied(v.filters_applied || {});
                    setFiltersDraft(v.filters_applied || {});
                    if (Array.isArray(v.columns) && v.columns.length > 0) {
                      setClientsPrefs((prev) => ({ ...prev, table_columns: v.columns, active_custom_view_id: v.id }));
                    } else {
                      setClientsPrefs((prev) => ({ ...prev, active_custom_view_id: v.id }));
                    }
                    saveClientsPrefs({ ...clientsPrefs, active_custom_view_id: v.id, table_columns: Array.isArray(v.columns) ? v.columns : clientsPrefs.table_columns });
                    setOffset(0);
                  }}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid={`clients-custom-view-${v.id}`}
                >
                  {v.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => setCustomViewsOpen(true)}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="clients-custom-view-manage"
              >
                Manage Views…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="clients-bulk-actions"
                >
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                <DropdownMenuItem
                  onClick={openExportDialog}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid="clients-export-action"
                >
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                {/* MA-PHASE10: Hide Delete if user lacks leads module */}
                {canManageClients && (
                  <DropdownMenuItem
                    onClick={onDeleteSelected}
                    className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                    data-testid="clients-actions-delete"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <CollapsibleSearch
            value={q}
            onChange={setQ}
            placeholder="Search clients..."
            width="w-[300px]"
            data-testid="clients-search"
          />

          {/* Filter Button - between search and columns */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setZohoFiltersOpen(!zohoFiltersOpen)}
            aria-label="Open filters"
            data-testid="clients-filter-icon"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${zohoFiltersOpen ? "text-indigo-400" : ""}`} />
            {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" data-testid="clients-filter-active-indicator" /> : null}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={openColumnsDialog}
            disabled={prefsLoading}
            aria-label="Customize columns"
            data-testid="clients-manage-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          <Button
            onClick={openExportDialog}
            data-testid="clients-export-trigger"
          >
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (zohoFiltersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {zohoFiltersOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="clients-filters-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={zohoFiltersOpen}
                onOpenChange={setZohoFiltersOpen}
                title="Filter Clients by"
                fields={filterFieldDefs}
                optionsByField={optionsByField}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={applyZohoDraft}
                onClear={clearZohoFilters}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card ref={clientsCardRef} className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="clients-all-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading…
              </div>
            ) : (
            <div data-testid="clients-table-wrap" className="flex-1 min-w-0 overflow-hidden">
              <div
                className="h-full w-full min-w-0 overflow-auto"
                data-testid="clients-hscroll"
                onWheel={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.scrollLeft += e.deltaY;
                  }
                }}
              >
                <Table style={{ minWidth: tableColumns.length * 160 + 170 }} containerClassName="h-full">
                  <TableHeader className="relative">
                    <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-3 bg-white/10" />
                    <TableRow className="border-transparent hover:bg-transparent">
                      <TableHead colSpan={tableColumns.length + 1} className="h-auto p-1 text-slate-200/70">
                        <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={allOnPageSelected}
                              onCheckedChange={toggleAllOnPage}
                              className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              data-testid="clients-select-all"
                            />
                          </div>

                          <div className="min-w-0 grid items-center gap-3 pr-2" style={columnsGridStyle}>
                            {tableColumns.map((k) => (
                              <div key={k} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs" data-testid={`clients-table-head-${k}`}>
                                <span className="min-w-0 flex-1 truncate">{prettifyFieldLabel(k)}</span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className={
                                        "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                        (sortBy === k ? " bg-white/10" : "")
                                      }
                                      aria-label={`Sort ${prettifyFieldLabel(k)}`}
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`clients-sort-${k}`}
                                    >
                                      <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                    </button>
                                  </DropdownMenuTrigger>

                                  <DropdownMenuContent className="glass border-white/10 text-slate-100" align="end">
                                    <DropdownMenuItem
                                      onClick={() => applySort(k, "asc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                      data-testid={`clients-sort-${k}-asc`}
                                    >
                                      <ArrowDownAZ className="mr-2 h-4 w-4" /> Sort Ascending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => applySort(k, "desc")}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                      data-testid={`clients-sort-${k}-desc`}
                                    >
                                      <ArrowUpAZ className="mr-2 h-4 w-4" /> Sort Descending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={clearSort}
                                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                      data-testid={`clients-sort-${k}-clear`}
                                    >
                                      Clear sort
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>

                          <div className="h-9 w-9" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedItems.length === 0 ? (
                      <TableRow className="border-transparent">
                        <TableCell colSpan={tableColumns.length + 1} className="py-16">
                          <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                            <Briefcase className="h-12 w-12 text-indigo-400 mb-3" />
                            <div className="text-sm text-slate-200/70">No clients found.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : pagedItems.map((client) => (
                        <TableRow key={client.id} className="border-transparent">
                          <TableCell colSpan={tableColumns.length + 1} className="p-1 align-top">
                            <div
                              className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(buildPath(`/app/clients/${client.id}`))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  navigate(buildPath(`/app/clients/${client.id}`));
                                }
                              }}
                              data-testid={`clients-row-${client.id}`}
                            >
                              <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selected.has(client.id)}
                                    onCheckedChange={() => toggleOne(client.id)}
                                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                    data-testid={`clients-select-${client.id}`}
                                  />
                                </div>
                                <div className="min-w-0 grid grid-cols-1">
                                  <div className="grid items-center gap-3 pr-2" style={columnsGridStyle}>
                                    {tableColumns.map((k) => {
                                      const value = getClientFieldValue(client, k);
                                      return (
                                        <div key={k} className="min-w-0" data-testid={`clients-cell-${client.id}-${k}`}>
                                          {k === "insurance_type" ? (
                                            <Badge className={"ring-1 " + pillClass(value || "—")} data-testid={`clients-badge-${client.id}-insurance`}>
                                              {value || "—"}
                                            </Badge>
                                          ) : k === "company" ? (
                                            <Badge className={"ring-1 " + pillClass(value || "—")} data-testid={`clients-badge-${client.id}-company`}>
                                              {value || "—"}
                                            </Badge>
                                          ) : k === "product_name" ? (
                                            <Badge className={"ring-1 " + pillClass(value || "—")} data-testid={`clients-badge-${client.id}-product`}>
                                              {value || "—"}
                                            </Badge>
                                          ) : k === "product_status" ? (
                                            <Badge
                                              className={`ring-1 ${productStatusPill(value)}`}
                                              data-testid={`clients-badge-${client.id}-product-status`}
                                            >
                                              {value || "—"}
                                            </Badge>
                                          ) : k === "product_sub_status" ? (
                                            <Badge
                                              className={`ring-1 ${pillClass(value || "—")}`}
                                              data-testid={`clients-badge-${client.id}-product-sub-status`}
                                            >
                                              {value || "—"}
                                            </Badge>
                                          ) : k === "full_name" ? (
                                            <div className="truncate font-medium text-slate-100" data-testid={`clients-name-${client.id}`}>
                                              {value || "—"}
                                            </div>
                                          ) : (
                                            <div className="truncate text-slate-100/90" data-testid={`clients-value-${client.id}-${k}`}>
                                              {formatCellValue(k, value)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </div>
            </div>
            )}

            <div data-testid="clients-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70" data-testid="clients-total-count">
                {anySelected ? `Selected: ${selected.size}` : `Total Clients: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="clients-pagination">
                {offset > 0 ? (
                  <Button
                    variant="secondary"
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    data-testid="clients-prev-page"
                  >
                    Previous
                  </Button>
                ) : null}

                {offset + limit < total ? (
                  <Button
                    variant="secondary"
                    onClick={() => setOffset((o) => o + limit)}
                    data-testid="clients-next-page"
                  >
                    Next
                  </Button>
                ) : null}

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    const next = Number(v);
                    setLimit(next);
                    setOffset(0);
                    saveClientsPrefs({ ...clientsPrefs, page_size: next });
                  }}
                >
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20" data-testid="clients-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`clients-page-size-${n}`}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <LeadTableColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        fields={CLIENT_FIELD_OPTIONS}
        value={tableColumns}
        onSave={(nextColumns) => {
          // P0 FIX: Save columns per-view for proper persistence
          const nextViewColumns = {
            ...(clientsPrefs.view_columns || {}),
            [activeCustomViewId]: nextColumns,
          };
          const nextPrefs = {
            ...clientsPrefs,
            table_columns: nextColumns,
            view_columns: nextViewColumns,
          };
          // Also update all_table_columns for "all" view for backward compatibility
          if (activeCustomViewId === "all") {
            nextPrefs.all_table_columns = nextColumns;
          }
          saveClientsPrefs(nextPrefs);
        }}
      />

      <CustomViewsDialog
        open={customViewsOpen}
        onOpenChange={setCustomViewsOpen}
        value={customViews}
        currentDraft={{ filters_applied: filtersApplied }}
        currentColumns={tableColumns}
        onSave={(nextViews) => {
          const nextPrefs = {
            ...clientsPrefs,
            custom_views: nextViews,
            active_custom_view_id: activeCustomViewId,
          };
          saveClientsPrefs(nextPrefs);
        }}
      />

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl">
          <DialogHeader>
            <DialogTitle>Export CSV</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs text-slate-200/70">Scope</div>
              <Select value={exportScope} onValueChange={setExportScope}>
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10" data-testid="clients-export-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  <SelectItem value="selected" data-testid="clients-export-scope-selected">Selected Clients</SelectItem>
                  <SelectItem value="filtered" data-testid="clients-export-scope-filtered">All Clients (Matching Filters)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-200/60" data-testid="clients-export-scope-count">
                {exportScope === "selected" ? `Selected: ${selected.size}` : `Matching current filters: ${total}`}
              </div>
            </div>

            <div className="flex items-end justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setExportFields(new Set(CLIENT_FIELDS))}
                data-testid="clients-export-all-fields"
              >
                All Fields
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setExportFields(new Set())}
                data-testid="clients-export-none-fields"
              >
                None
              </Button>
            </div>
          </div>

          <div className="max-h-[360px] overflow-auto rounded-xl bg-black/20 p-3 ring-1 ring-white/10" data-testid="clients-export-fields">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {CLIENT_FIELDS.map((f, idx) => (
                <label key={f} className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-white/5" data-testid={`clients-export-field-${idx}`}>
                  <Checkbox
                    checked={exportFields.has(f)}
                    onCheckedChange={(v) => {
                      setExportFields((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(f);
                        else next.delete(f);
                        return next;
                      });
                    }}
                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                    data-testid={`clients-export-field-toggle-${idx}`}
                  />
                  <span className="font-mono text-xs text-slate-200/80" data-testid={`clients-export-field-label-${idx}`}>{f}</span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setExportOpen(false)}
              disabled={exporting}
              data-testid="clients-export-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={runExport}
              disabled={exporting}
              data-testid="clients-export-submit"
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10" data-testid="clients-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-300">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedRows.length} Client{selectedRows.length !== 1 ? "s" : ""}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300/80">
              Are you sure you want to delete {selectedRows.length === 1 ? "this client" : `these ${selectedRows.length} clients`}? 
              This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Preview of clients to be deleted */}
          {selectedRows.length > 0 && selectedRows.length <= 5 && (
            <div className="rounded-lg bg-white/5 p-3 space-y-2">
              {selectedRows.map((client) => (
                <div key={client.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-100 font-medium">{client.name || "Unnamed"}</span>
                  <span className="text-slate-400">{client.email || "No email"}</span>
                </div>
              ))}
            </div>
          )}
          {selectedRows.length > 5 && (
            <div className="rounded-lg bg-white/5 p-3 text-center text-sm text-slate-400">
              {selectedRows.length} clients selected for deletion
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button
                variant="secondary"
                data-testid="clients-delete-cancel"
              >
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleting}
                data-testid="clients-delete-confirm"
              >
                {deleting ? "Deleting…" : `Delete ${selectedRows.length} Client${selectedRows.length !== 1 ? "s" : ""}`}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
